/**
 * The error codes are used by frontend and backend, which is why we need to have them separately from the error collection
 * that is only used by the frontend and requires translation
 *
 * ERROR CODE NUMBERING CONVENTION:
 * - HTTP status codes: 400, 401, 403, 404, 500 (standard)
 * - Custom 5-digit codes follow pattern: [CategoryPrefix][Sequence]
 *
 * Category Ranges:
 * - 40001-40099: Session management errors (client-side validation - frontend only)
 * - 40101-40199: Authentication/authorization errors (frontend only)
 * - 40201-40299: Brand/market validation errors (frontend only)
 * - 40301-40399: Legal/terms errors (frontend only)
 * - 40401-40499: Carline management errors
 * - 40501-40599: Tenant configuration errors
 * - 40601-40699: Analytics errors
 * - 40701-40799: Signaling/WebRTC errors
 * - 40801-40899: Notification errors
 * - 40901-40999: Scheduling errors
 * - 41001-41099: Tenant management errors
 * - 50001-50099: Server-side session errors
 * - 60001-60099: Vehicle validation errors
 * - 70001-70099: Streaming/visualization errors
 * - 80001-80099: Showroom/AVE errors (frontend only)
 * - 90001-90099: Image/2D service errors
 * - 100001-100099: Digital Giveaway errors
 * - 110001-110099: ICE server errors
 */

export enum EErrorCode {
  // HTTP Status Codes (auto-mapped by LambdaResponse)
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  ACCESS_DENIED = 403,
  NOT_FOUND = 404,
  INTERNAL_ERROR = 500,

  // Session Management (40001-40099) - Frontend only
  INVALID_SESSION_ID = 40001,
  CANT_LOAD_SESSION = 40002,
  SESSION_REQUIRED = 40003,
  SESSION_BRAND_REQUIRED = 40004,
  SESSION_DATA_REQUIRED = 40005,
  SESSION_AUTH_REQUIRED = 40006,
  SESSION_ORGANIZER_ONLY = 40007,
  SESSION_CREATE_FAILED = 40008,
  SESSION_UPDATE_FAILED = 40009,
  SESSION_DELETE_FAILED = 40010,
  SESSION_POPUP_BLOCKED = 40011,
  SESSION_STALE_WRITE = 40012, // HTTP 409 — optimistic concurrency conflict

  // Authentication/Authorization (40101-40199) - Frontend only
  USER_TOKENS_MISSING = 40101,

  // Brand/Market Validation (40201-40299) - Frontend only
  INVALID_BRAND = 40201,
  INVALID_MARKET = 40202,
  DISABLED_MARKET = 40203,

  // Legal/Terms (40301-40399) - Frontend only
  TERMS_OF_USE = 40301,
  LEGAL_DOCS = 40302,
  DEALER_NOT_WHITELISTED = 40304,

  // Carline Management (40401-40499)
  CARLINE_BRAND_REQUIRED = 40401,
  CARLINE_ID_REQUIRED = 40402,
  CARLINE_DATA_REQUIRED = 40403,
  CARLINE_MARKET_REQUIRED = 40404,
  CARLINE_FETCH_FAILED = 40405,
  CARLINE_CREATE_FAILED = 40406,
  CARLINE_UPDATE_FAILED = 40407,
  CARLINE_DELETE_FAILED = 40408,

  // Tenant Configuration (40501-40599)
  TENANT_CONFIG_BRAND_MARKET_REQUIRED = 40501,
  TENANT_CONFIG_ID_REQUIRED = 40502,
  TENANT_CONFIG_DATA_REQUIRED = 40503,
  TENANT_CONFIG_NOT_FOUND = 40504,
  TENANT_CONFIG_ALREADY_EXISTS = 40505,
  TENANT_CONFIG_FETCH_FAILED = 40506,
  TENANT_CONFIG_CREATE_FAILED = 40507,
  TENANT_CONFIG_UPDATE_FAILED = 40508,
  TENANT_CONFIG_DELETE_FAILED = 40509,

  // Analytics (40601-40699)
  ANALYTICS_METRIC_REQUIRED = 40601,
  ANALYTICS_METRIC_INVALID = 40602,
  ANALYTICS_PAYLOAD_REQUIRED = 40603,
  ANALYTICS_PAYLOAD_INVALID = 40604,
  ANALYTICS_CREATE_FAILED = 40605,

  // Signaling/WebRTC (40701-40799)
  SIGNALING_BODY_REQUIRED = 40701,
  SIGNALING_EVENT_INVALID = 40702,
  SIGNALING_CONNECTION_FAILED = 40703,
  SIGNALING_HANDSHAKE_FAILED = 40704,
  SIGNALING_RELAY_FAILED = 40705,

  // Notifications (40801-40899)
  NOTIFICATION_CREATE_FAILED = 40801,

  // Scheduling (40901-40999)
  SCHEDULE_OVERLAP = 409, // HTTP 409 Conflict
  SCHEDULE_START_TIME_REQUIRED = 40901,
  SCHEDULE_START_TIME_INVALID = 40902,
  SCHEDULE_END_TIME_INVALID = 40903,
  SCHEDULE_LIMIT_INVALID = 40904,
  SCHEDULE_SESSION_ID_REQUIRED = 40905,
  SCHEDULE_SESSION_DATA_REQUIRED = 40906,
  SCHEDULE_NOT_AUTHORIZED = 40907,
  SCHEDULE_CREATE_FAILED = 40908,
  SCHEDULE_UPDATE_FAILED = 40909,
  SCHEDULE_DELETE_FAILED = 40910,
  SCHEDULE_FETCH_FAILED = 40911,

  // Tenant Management (41001-41099)
  TENANT_NAME_REQUIRED = 41001,
  TENANT_FETCH_FAILED = 41002,
  TENANT_CREATE_FAILED = 41003,

  // Server-side Session Errors (50001-50099)
  SESSION_NOT_FOUND = 50001,

  // Vehicle Validation (60001-60099)
  INVALID_CODE = 60001,
  VEHICLE_NOT_FOUND = 60002,
  WLTP_INVALID = 60003,
  OUTDATED_VEHICLE = 60004,
  UPSTREAM_ERROR = 60005,
  UPSTREAM_TIMEOUT = 60006,

  // Streaming/Visualization (70001-70099)
  GENERAL_STREAM_ERROR = 70001,

  // Showroom/AVE (80001-80099) - Frontend only
  UNKNOWN_EXCEPTION = 80001,
  AVE_CAR_NOT_INSTALLED = 80002,
  AVE_ENVIRONMENT_NOT_INSTALLED = 80003,
  NO_INSTANCES_AVAILABLE = 80004,
  PR_STRING_NOT_PROVIDED = 80005,
  CONFIGURATION_ERROR = 80006,
  BRAND_NOT_PROVIDED = 80007,
  BRAND_NOT_SUPPORTED = 80008,
  SERVICE_NOT_PROVIDED = 80009,
  MARKET_NOT_PROVIDED = 80010,

  // Image/2D Service (90001-90099)
  IMAGES_NOT_AVAILABLE = 90001,
  IMAGES_NOT_ENABLED = 90002,

  // Digital Giveaway (100001-100099)
  DGA_ERROR = 100001,
  DGA_AUTH_ERROR = 100002,

  // ICE Servers (110001-110099)
  ICE_SERVERS_FAILED = 110001,
}

// Disable because we explicitly want to check the type of an input that could be anything
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isEErrorCode(input: any): input is EErrorCode {
  return Object.values(EErrorCode).includes(input);
}
